/**
 * Today screen — main dashboard.
 *
 * Layout matches mobile.html screen 2:
 *   - Header: weekday + greeting + avatar.
 *   - MoonCard (phase + position + headline + body).
 *   - StatsRow (7-day check-in streak — simple, no gamification XP).
 *   - 3 transit rows.
 *   - "Открыть карту" CTA → /chart.
 *
 * Data: fetchToday() — MSW-style mock fixture in Day 1-3, real API on Day 4.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { MoonCard } from "@/components/MoonCard";
import { TabBar } from "@/components/TabBar";
import { TransitRow } from "@/components/TransitRow";
import { fetchToday, type TodayResponse } from "@/lib/api";
import { resolveTimezone } from "@/lib/lunar-api";
import { hapticImpact } from "@/lib/tg";
import { useTgContext } from "@/state/tg";

export default function TodayPage(): ReactElement {
  const tg = useTgContext();
  const tz = resolveTimezone();
  const firstName = tg.user?.firstName ?? "Алина";
  const initial = tg.user?.initial ?? "А";

  const { data, isLoading, error } = useQuery<TodayResponse>({
    queryKey: ["today", tg.initData ?? "guest", tz],
    queryFn: () => fetchToday(tg.initData, tz),
    staleTime: 5 * 60_000,
  });

  return (
    <main className="screen starry">
      <div className="body-pad has-tabbar">
        <header className="today-head">
          <div className="today-greet">
            <div className="day">{data?.greeting ?? "Понедельник · 11 мая"}</div>
            <div className="name">Доброе утро, {firstName}</div>
          </div>
          <div className="avatar" aria-hidden="true">
            {initial}
          </div>
        </header>

        {isLoading ? <SkeletonBody /> : null}
        {error ? <ErrorBanner message={(error as Error).message} /> : null}

        {data && !data.onboarded ? <NotOnboarded /> : null}

        {data?.onboarded ? (
          <>
            <MoonCard
              phaseLabel={data.moonPhase.label}
              position={data.moonPhase.position}
              headline={data.moonPhase.headline}
              body={data.moonPhase.body}
            />

            {data.streak ? (
              <StatsRow
                days={data.streak.days}
                target={data.streak.target}
                todayChecked={data.streak.todayChecked}
              />
            ) : null}

            <h2 className="section-h">
              <span>Транзиты сегодня</span>
              <Link to="/transits">все →</Link>
            </h2>

            {data.transits.map((t) => (
              <TransitRow
                key={t.id}
                glyph={t.icon}
                title={t.title}
                time={t.time}
                aspect={t.aspect}
              />
            ))}

            <Link to="/chart" className="today-cta" onClick={() => hapticImpact("light")}>
              <span>Открыть мою карту</span>
              <span className="arrow" aria-hidden="true">
                ›
              </span>
            </Link>
          </>
        ) : null}
      </div>
      <TabBar active="today" />
    </main>
  );
}

/* ----- Streak indicator (7-day check-in, no XP/badges) ----- */

interface StatsRowProps {
  readonly days: number;
  readonly target: number;
  readonly todayChecked: boolean;
}

function StatsRow({ days, target, todayChecked }: StatsRowProps): ReactElement {
  const filled = Math.min(days, target);
  const todayIndex = todayChecked ? filled - 1 : filled;
  return (
    // biome-ignore lint/a11y/useSemanticElements: card row UI; fieldset has user-agent styling that breaks the .stats-row layout
    <div className="stats-row" role="group" aria-label={`Серия ${days} из ${target} дней`}>
      <div>
        <div className="label">Серия</div>
        <div className="value">
          {days} / {target} дней
        </div>
      </div>
      <div className="streak-dots" aria-hidden="true">
        {Array.from({ length: target }, (_, i) => {
          let cls = "streak-dot";
          if (i < filled) cls += " done";
          if (i === todayIndex && todayChecked) cls += " today";
          return <span key={i} className={cls} />;
        })}
      </div>
    </div>
  );
}

function SkeletonBody(): ReactElement {
  return (
    <div role="status" aria-busy="true" aria-label="Загрузка дня" style={{ opacity: 0.4 }}>
      <div className="moon-card" style={{ minHeight: 120 }} />
      <div className="stats-row" style={{ minHeight: 56 }} />
    </div>
  );
}

function ErrorBanner({ message }: { readonly message: string }): ReactElement {
  return (
    <div className="ob-error" role="alert">
      Не получилось загрузить день: {message}
    </div>
  );
}

/**
 * Shown when the session is valid but the user has no birth data on file.
 * Dignified prompt — no fabricated transits or moon card (data-correctness).
 */
function NotOnboarded(): ReactElement {
  return (
    <article className="moon-card" role="note">
      <h3 style={{ margin: "0 0 8px" }}>Заполните данные рождения</h3>
      <p style={{ margin: 0 }}>
        Чтобы рассчитать ваши транзиты и натальную карту, добавьте дату, время и место рождения в
        кабинете. Без них мы не показываем выдуманные данные.
      </p>
    </article>
  );
}
